export function parseCardMinor(value) {
  const match = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(String(value).trim());
  if (!match) throw new Error("amount.invalid");
  const minor = Number(match[1]) * 100 + Number((match[2] || "").padEnd(2, "0"));
  if (minor <= 0 || minor > 100000000) throw new Error("amount.invalid");
  return minor;
}

export function cardAmount(minor, currency = "USD") {
  if (!Number.isInteger(minor)) return "—";
  return currency === "USD" ? `$${(minor / 100).toFixed(2)}` : `${(minor / 100).toFixed(2)} ${currency}`;
}

export function issueBalanceSummary(wallet, quote) {
  if (!wallet || quote?.operation !== "issue") return null;
  const amount = wallet.walletMicros;
  const hold = wallet.walletHoldMicros;
  const cost = quote.chargedMinor;
  const debit = quote.usedFreeMinor;
  if (![amount, hold, cost, debit].every(Number.isSafeInteger)
    || amount < 0 || hold < 0 || cost < 0 || debit !== cost
    || quote.usedRestrictedMinor !== 0 || !Number.isSafeInteger(debit * 10000)) return null;
  const availableMicros = Math.max(0, amount - hold);
  const remaining = availableMicros - debit * 10000;
  return { availableMicros, costMinor: cost, remainingMicros: remaining >= 0 ? remaining : null };
}

export function topupBalanceSummary(wallet, quote, received) {
  if (!wallet || quote?.operation !== "topup" || quote.requestedAmountOnCardMinor !== received
    || quote.amountOnCardMinor !== received) return null;
  const { chargedMinor: charge, usedFreeMinor: free, usedRestrictedMinor: restricted } = quote;
  if (![received, charge, free, restricted, wallet.walletMicros, wallet.walletHoldMicros].every(Number.isSafeInteger)
    || received <= 0 || charge < received || free < 0 || restricted < 0 || free + restricted !== charge
    || wallet.walletMicros < 0 || wallet.walletHoldMicros < 0 || !Number.isSafeInteger(free * 10000)) return null;
  const available = Math.max(0, wallet.walletMicros - wallet.walletHoldMicros);
  const remaining = available - free * 10000;
  return { receivedMinor: received, feeMinor: charge - received, chargeMinor: charge, freeMinor: free,
    restrictedMinor: restricted, availableMicros: available, remainingMicros: remaining >= 0 ? remaining : null };
}

export function withdrawBalanceSummary(wallet, quote, requested) {
  if (!wallet || quote?.operation !== "return" || quote.returnDestination !== "lightpay_wallet"
    || (requested === "all" ? quote.returnAll !== true : quote.amountMinor !== requested)) return null;
  const amount = quote.amountMinor, credit = quote.returnWalletCreditMinor, fee = quote.returnFeeMinor;
  const balance = quote.returnCardBalanceMinor;
  if (![amount, credit, fee, balance, wallet.walletMicros, wallet.walletHoldMicros].every(Number.isSafeInteger)
    || amount <= 0 || credit <= 0 || fee < 0 || credit + fee !== amount || amount > balance
    || wallet.walletMicros < 0 || wallet.walletHoldMicros < 0 || !Number.isSafeInteger(credit * 10000)) return null;
  const available = Math.max(0, wallet.walletMicros - wallet.walletHoldMicros);
  const after = available + credit * 10000;
  if (!Number.isSafeInteger(after)) return null;
  return { amountMinor: amount, creditMinor: credit, feeMinor: fee, cardBalanceMinor: balance,
    cardRemainingMinor: balance - amount, walletAfterMicros: after };
}

export function operationOpen(operation) {
  return ["prepared", "sending", "pending", "unknown", "review"].includes(operation.status);
}

// A local wallet job and an issuer transaction are different records. Only
// collapse them when the provider supplied the exact transaction identity.
export function cardTimeline(cardId, operations, transactions, copy, label) {
  // Issuer history can contain a top-up while omitting a completed withdrawal.
  // Keep each local receipt; only remove an issuer row linked by its exact ID.
  const jobs = operations.filter((op) => (op.cardId || op.result?.cardId) === cardId);
  const knownIds = new Set(jobs.flatMap((op) => [op.result?.transactionId,
    op.operation === "topup" ? op.result?.topupId : null]).filter(Boolean));
  const rows = transactions.filter((tx) => !knownIds.has(tx.id)).map((tx) => ({
    ...tx,
    status: tx.status === "failed" ? "declined" : tx.status === "success" ? "completed" : tx.status,
    ...(tx.source === "wallet" ? {
      source: "ours", type: tx.type === "withdrawal" ? "unload" : "fund",
      displayTitle: label(tx.type === "withdrawal" ? "return" : "topup"),
      displaySubtitle: label(tx.status), displayStatus: label(tx.status),
    } : {}),
  }));
  for (const op of jobs) {
    const done = op.status === "completed";
    const credited = done && op.operation === "topup" && Number.isInteger(op.result?.amountOnCardMinor);
    const returned = op.operation === "return";
    const walletReturn = returned && (op.result?.returnDestination || op.returnDestination) === "lightpay_wallet";
    const amount = returned ? (op.result?.returnedMinor ?? op.quotedReturnMinor) : credited ? op.result.amountOnCardMinor : op.chargedMinor;
    const created = Number(op.createdAt);
    const occurredAt = Number.isFinite(created) && created > 0 ? new Date(created * 1000).toISOString() : null;
    const detailRows = [];
    if (op.operation === "topup") {
      detailRows.push([copy.debit, cardAmount(op.chargedMinor)]);
      if (op.result?.amountOnCardMinor != null) detailRows.push([copy.net, cardAmount(op.result.amountOnCardMinor)]);
    }
    if (op.chargedFreeMinor > 0) detailRows.push([copy.fromWallet, `${(op.chargedFreeMinor / 100).toFixed(2)} USDT`]);
    if (op.chargedRestrictedMinor > 0) detailRows.push([copy.fromRestricted, cardAmount(op.chargedRestrictedMinor)]);
    if (returned) detailRows.push(walletReturn
      ? [done ? copy.toWallet || "LightPay" : copy.toWalletPending || "Expected in LightPay", cardAmount(done ? op.result?.walletCreditMinor : op.quotedWalletCreditMinor, "USDT")]
      : [copy.restricted, cardAmount(op.result?.returnedMinor)]);
    rows.push({
      id: op.operationId, source: "ours", type: op.operation === "issue" ? "issue" : returned ? "unload" : "fund",
      amountMinor: amount, currency: returned || credited ? "USD" : "USDT", scale: 2,
      direction: credited ? "in" : "out", occurredAt,
      status: operationOpen(op) ? "processing" : op.status,
      displayTitle: label(op.operation), displaySubtitle: [label(op.status), occurredAt && new Date(occurredAt).toLocaleDateString()].filter(Boolean).join(" · "),
      displayStatus: label(op.status), detailRows,
    });
  }
  const timestamp = (value) => {
    if (!value) return 0;
    const date = String(value).replace(" ", "T");
    return Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/i.test(date) ? date : date + "Z") || 0;
  };
  return rows.sort((a, b) => timestamp(b.occurredAt) - timestamp(a.occurredAt));
}
